import { useEffect, useRef, useState } from "react";
import { datasetSchema, orderedDataset, uid, type Dataset } from "@proof/core";
import { parseTabular, type TabularResult } from "@proof/tabular";
import { datasetFromSheet } from "./dataset-import";
import { Button, Control, Toggle } from "./components";

export function newDataset(kind: "choices" | "table" = "choices"): Dataset {
  return datasetSchema.parse({
    id: uid("dataset"),
    name: "New dataset",
    kind,
    ...(kind === "table"
      ? {
          columns: [
            { key: "id", label: "ID" },
            { key: "name", label: "Name" },
          ],
          primaryKey: "id",
          labelColumn: "name",
          rows: [],
        }
      : {}),
  });
}
export function DatasetEditor({
  value: d,
  onChange,
}: {
  value: Dataset;
  onChange: (value: Dataset) => void;
}) {
  const [page, setPage] = useState(0),
    [lines, setLines] = useState(""),
    [error, setError] = useState(""),
    [upload, setUpload] = useState<TabularResult>(),
    [sheet, setSheet] = useState(0),
    [busy, setBusy] = useState(false);
  const ticket = useRef(0);
  useEffect(
    () => () => {
      ticket.current++;
    },
    [],
  );
  const change = (patch: Partial<Dataset>) => onChange({ ...d, ...patch });
  const move = (index: number, delta: number) => {
    const entries = d.kind === "choices" ? [...d.choices] : [...d.rows];
    const target = index + delta;
    if (target < 0 || target >= entries.length) return;
    [entries[index], entries[target]] = [entries[target], entries[index]];
    onChange({
      ...d,
      sort: undefined,
      ...(d.kind === "choices"
        ? { choices: entries as Dataset["choices"] }
        : { rows: entries as Dataset["rows"] }),
    });
  };
  const orderButtons = (index: number) => (
    <>
      <Button
        disabled={!!d.sort || index === 0}
        onClick={() => move(index, -1)}
      >
        Move up
      </Button>
      <Button
        disabled={!!d.sort || index + 1 >= items.length}
        onClick={() => move(index, 1)}
      >
        Move down
      </Button>
    </>
  );
  const items = d.kind === "choices" ? d.choices : d.rows;
  const current = Math.min(page, Math.max(0, Math.ceil(items.length / 20) - 1)),
    start = current * 20;
  const columnOptions = d.columns.map((c) => (
    <option key={c.key} value={c.key}>
      {c.label}
    </option>
  ));
  return (
    <div className="dataset-editor">
      <div className="control-grid">
        <Control label="Dataset name">
          <input
            value={d.name}
            onChange={(e) => change({ name: e.target.value })}
          />
        </Control>
        <Control label="Dataset type">
          <select
            value={d.kind}
            onChange={(e) => {
              change({
                ...newDataset(e.target.value as Dataset["kind"]),
                id: d.id,
                name: d.name,
                version: d.version,
                sort: undefined,
              });
              setPage(0);
            }}
          >
            <option value="choices">Simple list</option>
            <option value="table">Table</option>
          </select>
        </Control>
      </div>
      <div className="control-grid">
        <Control
          label="Default order"
          note="Saved and returned in this order. Keep manual order to arrange entries yourself."
        >
          <select
            value={d.sort?.property || ""}
            onChange={(event) =>
              change({
                sort: event.target.value
                  ? {
                      property: event.target.value,
                      direction: d.sort?.direction || "asc",
                    }
                  : undefined,
              })
            }
          >
            <option value="">Manual order</option>
            {(d.kind === "choices"
              ? [
                  { key: "label", label: "Label" },
                  { key: "value", label: "Value" },
                ]
              : d.columns
            ).map((column) => (
              <option key={column.key} value={column.key}>
                Order by {column.label}
              </option>
            ))}
          </select>
        </Control>
        {d.sort && (
          <Control label="Sort direction">
            <select
              value={d.sort.direction}
              onChange={(event) =>
                change({
                  sort: {
                    ...d.sort!,
                    direction: event.target.value as "asc" | "desc",
                  },
                })
              }
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </Control>
        )}
        {d.sort && (
          <Button onClick={() => onChange(orderedDataset(d))}>
            Apply order to entries
          </Button>
        )}
      </div>
      <details>
        <summary>Upload CSV, TSV or Excel data</summary>
        <p>
          Choose a file and worksheet, review the preview, then replace the
          editor data. Cells import as text; row IDs preserve source row
          numbers. Maximum 10 MB upload and 1 MB saved dataset.
        </p>
        <Control label="Upload dataset">
          <input
            type="file"
            accept=".csv,.tsv,.txt,.xlsx"
            disabled={busy}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              const request = ++ticket.current;
              setBusy(true);
              setError("");
              setUpload(undefined);
              try {
                if (file.size > 10000000)
                  throw new Error("Upload exceeds 10 MB");
                const result = await parseTabular(
                  new Uint8Array(await file.arrayBuffer()),
                  { previewRows: 10000, limits: { columns: 49 } },
                );
                if (request === ticket.current) {
                  setUpload(result);
                  setSheet(0);
                }
              } catch (e) {
                if (request === ticket.current) setError((e as Error).message);
              } finally {
                if (request === ticket.current) setBusy(false);
              }
            }}
          />
        </Control>
        {busy && <p role="status">Reading upload…</p>}
        {upload && (
          <>
            <Control label="Dataset worksheet">
              <select
                value={sheet}
                onChange={(e) => setSheet(Number(e.target.value))}
              >
                {upload.sheets.map((s, i) => (
                  <option key={i} value={i}>
                    {s.name} · {s.rowCount} rows
                  </option>
                ))}
              </select>
            </Control>
            {upload.diagnostics.length > 0 && (
              <ul>
                {upload.diagnostics.map((d, i) => (
                  <li key={i}>{d.message}</li>
                ))}
              </ul>
            )}
            {upload.sheets[sheet] && (
              <>
                <div className="dataset-table-scroll">
                  <table>
                    <thead>
                      <tr>
                        {upload.sheets[sheet].headers.map((h, i) => (
                          <th key={i}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {upload.sheets[sheet].rows.slice(0, 5).map((r, i) => (
                        <tr key={i}>
                          {r.map((v, j) => (
                            <td key={j}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button
                  onClick={() => {
                    try {
                      onChange(datasetFromSheet(upload.sheets[sheet], d));
                      setUpload(undefined);
                      setError("");
                      setPage(0);
                    } catch (e) {
                      setError((e as Error).message);
                    }
                  }}
                >
                  Replace with uploaded data
                </Button>
              </>
            )}
          </>
        )}
      </details>
      {error && <p role="alert">{error}</p>}
      {d.kind === "choices" ? (
        <>
          <Control
            label="Paste list items"
            note="One item per line, or value<TAB>label."
          >
            <textarea
              rows={3}
              value={lines}
              onChange={(e) => setLines(e.target.value)}
            />
          </Control>
          <Button
            onClick={() => {
              change({
                choices: [
                  ...d.choices,
                  ...lines
                    .split(/\r?\n/)
                    .filter((s) => s.trim())
                    .map((s) => {
                      const [value, label] = s.split("\t");
                      return { value, label: label ?? value };
                    }),
                ],
              });
              setLines("");
            }}
          >
            Append list items
          </Button>
          <Button
            onClick={() =>
              change({
                choices: [
                  ...d.choices,
                  { value: uid("item"), label: "New item" },
                ],
              })
            }
          >
            Add list item
          </Button>
          {d.choices.slice(start, start + 20).map((c, i) => (
            <div className="library-input-row" key={start + i}>
              {orderButtons(start + i)}
              <Control label="Value">
                <input
                  value={c.value}
                  onChange={(e) =>
                    change({
                      choices: d.choices.map((x, j) =>
                        j === start + i ? { ...x, value: e.target.value } : x,
                      ),
                    })
                  }
                />
              </Control>
              <Control label="Label">
                <input
                  value={c.label}
                  onChange={(e) =>
                    change({
                      choices: d.choices.map((x, j) =>
                        j === start + i ? { ...x, label: e.target.value } : x,
                      ),
                    })
                  }
                />
              </Control>
              <Button
                onClick={() =>
                  change({
                    choices: d.choices.filter((_, j) => j !== start + i),
                  })
                }
              >
                Remove item {start + i + 1}
              </Button>
            </div>
          ))}
        </>
      ) : (
        <>
          <p>
            Configure columns, then enter rows. Empty cells save as null.
            Changing a column type clears its cells.
          </p>
          {d.columns.map((c) => (
            <div className="library-input-row" key={c.key}>
              <Control label={`Column ${c.key} label`}>
                <input
                  value={c.label}
                  onChange={(e) =>
                    change({
                      columns: d.columns.map((x) =>
                        x.key === c.key ? { ...x, label: e.target.value } : x,
                      ),
                    })
                  }
                />
              </Control>
              <Control label={`Column ${c.key} type`}>
                <select
                  value={c.type}
                  onChange={(e) =>
                    change({
                      columns: d.columns.map((x) =>
                        x.key === c.key
                          ? { ...x, type: e.target.value as typeof c.type }
                          : x,
                      ),
                      rows: d.rows.map((r) => ({ ...r, [c.key]: null })),
                    })
                  }
                >
                  <option value="string">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                </select>
              </Control>
              {c.type === "string" && (
                <Toggle
                  label={`Column ${c.key} multiline`}
                  value={!!c.multiline}
                  onChange={(multiline) =>
                    change({
                      columns: d.columns.map((column) =>
                        column.key === c.key
                          ? { ...column, multiline }
                          : column,
                      ),
                    })
                  }
                />
              )}
              <Button
                disabled={d.columns.length === 1}
                onClick={() => {
                  const columns = d.columns.filter((x) => x.key !== c.key);
                  const patch: Partial<Dataset> = {
                    columns,
                    rows: d.rows.map((r) =>
                      Object.fromEntries(
                        Object.entries(r).filter(([k]) => k !== c.key),
                      ),
                    ),
                  };
                  for (const k of [
                    "primaryKey",
                    "valueColumn",
                    "labelColumn",
                    "imageColumn",
                    "assetColumn",
                  ] as const)
                    if (d[k] === c.key)
                      patch[k] =
                        k === "primaryKey" ? columns[0].key : undefined;
                  if (d.sort?.property === c.key) patch.sort = undefined;
                  change(patch);
                }}
              >
                Remove column {c.label}
              </Button>
            </div>
          ))}
          <Button
            disabled={d.columns.length >= 50}
            onClick={() =>
              change({
                columns: [
                  ...d.columns,
                  { key: uid("column"), label: "New column", type: "string" },
                ],
              })
            }
          >
            Add column
          </Button>
          <div className="control-grid">
            {(
              [
                "primaryKey",
                "valueColumn",
                "labelColumn",
                "imageColumn",
                "assetColumn",
              ] as const
            ).map((k) => (
              <Control
                key={k}
                label={
                  k === "primaryKey"
                    ? "Primary key column"
                    : k === "valueColumn"
                      ? "Value column"
                      : k === "labelColumn"
                        ? "Label column"
                        : k === "imageColumn"
                          ? "Image URL column"
                          : "Managed asset ID column"
                }
              >
                <select
                  value={d[k] || ""}
                  onChange={(e) => change({ [k]: e.target.value || undefined })}
                >
                  {k !== "primaryKey" && (
                    <option value="">
                      {k === "imageColumn" || k === "assetColumn"
                        ? "None"
                        : "Use primary key"}
                    </option>
                  )}
                  {columnOptions}
                </select>
              </Control>
            ))}
          </div>
          <div className="dataset-table-scroll">
            <table>
              <thead>
                <tr>
                  {d.columns.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.slice(start, start + 20).map((row, i) => (
                  <tr key={start + i}>
                    {d.columns.map((c) => (
                      <td key={c.key}>
                        {c.type === "boolean" ? (
                          <select
                            aria-label={`Row ${start + i + 1} ${c.label}`}
                            value={
                              row[c.key] === null || row[c.key] === undefined
                                ? ""
                                : String(row[c.key])
                            }
                            onChange={(e) =>
                              change({
                                rows: d.rows.map((r, j) =>
                                  j === start + i
                                    ? {
                                        ...r,
                                        [c.key]:
                                          e.target.value === ""
                                            ? null
                                            : e.target.value === "true",
                                      }
                                    : r,
                                ),
                              })
                            }
                          >
                            <option value="">Empty</option>
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        ) : c.type === "string" && c.multiline ? (
                          <textarea
                            aria-label={`Row ${start + i + 1} ${c.label}`}
                            rows={3}
                            value={String(row[c.key] ?? "")}
                            onChange={(event) =>
                              change({
                                rows: d.rows.map((item, index) =>
                                  index === start + i
                                    ? {
                                        ...item,
                                        [c.key]: event.target.value || null,
                                      }
                                    : item,
                                ),
                              })
                            }
                          />
                        ) : (
                          <input
                            aria-label={`Row ${start + i + 1} ${c.label}`}
                            type={c.type === "number" ? "number" : "text"}
                            value={String(row[c.key] ?? "")}
                            onChange={(e) =>
                              change({
                                rows: d.rows.map((r, j) =>
                                  j === start + i
                                    ? {
                                        ...r,
                                        [c.key]:
                                          e.target.value === ""
                                            ? null
                                            : c.type === "number"
                                              ? Number(e.target.value)
                                              : e.target.value,
                                      }
                                    : r,
                                ),
                              })
                            }
                          />
                        )}
                      </td>
                    ))}
                    <td>
                      {orderButtons(start + i)}
                      <Button
                        onClick={() =>
                          change({
                            rows: d.rows.filter((_, j) => j !== start + i),
                          })
                        }
                      >
                        Remove row {start + i + 1}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            onClick={() => {
              change({
                rows: [
                  ...d.rows,
                  {
                    [d.primaryKey || d.columns[0].key]:
                      d.columns.find((c) => c.key === d.primaryKey)?.type ===
                      "number"
                        ? d.rows.length + 1
                        : uid("row"),
                  },
                ],
              });
              setPage(Math.floor(d.rows.length / 20));
            }}
          >
            Add row
          </Button>
        </>
      )}
      <div className="button-row">
        <Button disabled={current === 0} onClick={() => setPage(current - 1)}>
          Previous data page
        </Button>
        <span>
          {items.length} items · page {current + 1}
        </span>
        <Button
          disabled={start + 20 >= items.length}
          onClick={() => setPage(current + 1)}
        >
          Next data page
        </Button>
      </div>
    </div>
  );
}
